import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, FileText, MessageSquare, CreditCard, HardDrive, TrendingUp, Building2, User, Wallet } from 'lucide-react';

const StatCard = ({ title, value, subValue, icon: Icon, iconColor, bgColor, loading }) => (
    <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm">
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-slate-400 text-sm font-medium">{title}</p>
                    <p className="text-3xl font-bold text-white mt-2">{loading ? '...' : value}</p>
                    {subValue && (
                        <p className="text-sm text-slate-500 mt-1">{subValue}</p>
                    )}
                </div>
                <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                </div>
            </div>
        </CardContent>
    </Card>
);

const AdminDashboardPage = () => {
    const { api } = useAdmin();
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/admin/dashboard/stats');
                setStats(response.data);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [api]);

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div>
                <h1 className="text-2xl font-bold text-white">{t('admin.dashboardOverview')}</h1>
                <p className="text-slate-400 mt-1">{t('admin.platformStats')}</p>
            </div>

            {/* Main Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title={t('admin.totalUsers')}
                    value={stats?.users?.total || 0}
                    subValue={`+${stats?.users?.new_30d || 0} ${t('admin.last30Days')}`}
                    icon={Users}
                    iconColor="text-teal-400"
                    bgColor="bg-teal-500/20"
                    loading={loading}
                />
                <StatCard
                    title={t('admin.totalReports')}
                    value={stats?.reports?.total || 0}
                    subValue={`+${stats?.reports?.new_30d || 0} ${t('admin.last30Days')}`}
                    icon={FileText}
                    iconColor="text-blue-400"
                    bgColor="bg-blue-500/20"
                    loading={loading}
                />
                <StatCard
                    title={t('admin.aiConversations')}
                    value={stats?.ai_usage?.total_chats || 0}
                    subValue={`+${stats?.ai_usage?.chats_30d || 0} ${t('admin.last30Days')}`}
                    icon={MessageSquare}
                    iconColor="text-purple-400"
                    bgColor="bg-purple-500/20"
                    loading={loading}
                />
                <StatCard
                    title={t('admin.activeSubscriptions')}
                    value={stats?.subscriptions?.active || 0}
                    icon={CreditCard}
                    iconColor="text-amber-400"
                    bgColor="bg-amber-500/20"
                    loading={loading}
                />
            </div>

            {/* AI Cost tracking (estimated) */}
            <Card className="border-slate-700 bg-gradient-to-br from-teal-900/30 to-slate-800/50" data-testid="ai-cost-card">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center">
                                <Wallet className="w-6 h-6 text-teal-400" />
                            </div>
                            <div>
                                <p className="text-slate-300 font-medium">التكلفة الفعلية للذكاء الاصطناعي (تقديرية)</p>
                                <p className="text-xs text-slate-500">مبنية على عدد التوكنز وأسعار النموذج — قابلة للمعايرة</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-6 text-center">
                            <div>
                                <p className="text-2xl font-bold text-white" data-testid="ai-cost-avg">
                                    {loading ? '...' : (stats?.ai_cost?.avg_per_report_sar ?? 0)}
                                </p>
                                <p className="text-xs text-slate-400">متوسط تكلفة التحليل (ر.س)</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">
                                    {loading ? '...' : (stats?.ai_cost?.total_sar ?? 0)}
                                </p>
                                <p className="text-xs text-slate-400">إجمالي التكلفة (ر.س)</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">
                                    {loading ? '...' : (stats?.ai_cost?.tracked_reports ?? 0)}
                                </p>
                                <p className="text-xs text-slate-400">تحاليل مُحتسبة</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-slate-700 bg-slate-800/50">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <User className="w-5 h-5 text-teal-400" />
                            {t('admin.userBreakdown')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">{t('admin.individualUsers')}</span>
                                <span className="text-white font-medium">{stats?.users?.individual || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">{t('admin.laboratoryAccounts')}</span>
                                <span className="text-white font-medium">{stats?.users?.laboratory || 0}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-slate-700 bg-slate-800/50">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <HardDrive className="w-5 h-5 text-blue-400" />
                            {t('admin.storageUsage')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">{t('admin.totalStorage')}</span>
                                <span className="text-white font-medium">{stats?.storage?.total_mb || 0} {t('admin.mb')}</span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-2">
                                <div 
                                    className="bg-teal-500 h-2 rounded-full" 
                                    style={{ width: `${Math.min((stats?.storage?.total_mb || 0) / 100, 100)}%` }}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-slate-700 bg-slate-800/50">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-400" />
                            {t('admin.growth30Days')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">{t('admin.newUsers')}</span>
                                <span className="text-green-400 font-medium">+{stats?.users?.new_30d || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">{t('admin.reports')}</span>
                                <span className="text-green-400 font-medium">+{stats?.reports?.new_30d || 0}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminDashboardPage;
